// Undo tree data structure for emacs-style branching history

import type { RoomStyle } from "../types.ts";

/**
 * Serialized room for storage in deltas.
 * Converts Set<string> to string[] for JSON compatibility.
 */
export interface SerializedRoom {
  id: string;
  hexes: string[];
  label: string;
  userDrawn: boolean;
  style: RoomStyle;
}

/**
 * Serialized corridor for storage in deltas.
 */
export interface SerializedCorridor {
  id: string;
  hexes: string[];
  connects: [string, string];
  width: number;
  userDrawn: boolean;
}

/**
 * Tracks corridor connection updates for complex operations.
 */
export interface CorridorUpdate {
  corridorId: string;
  oldConnects: [string, string];
  newConnects: [string, string];
}

/**
 * Types of operations that create undo nodes.
 */
export type OperationType =
  | "addRoom"
  | "removeRoom"
  | "mergeRooms"
  | "extendRoom"
  | "moveRooms"
  | "scaleRooms"
  | "carveRoom"
  | "bisectRoom"
  | "labelRoom"
  | "addCorridor"
  | "removeCorridor"
  | "deleteScribble"
  | "initial";

/**
 * Delta stores what changed - enough to undo AND redo.
 * Each operation type has specific data needed for reversal.
 */
export type OperationDelta =
  | { type: "addRoom"; room: SerializedRoom }
  | { type: "removeRoom"; room: SerializedRoom; removedCorridors: SerializedCorridor[] }
  | { type: "addCorridor"; corridor: SerializedCorridor }
  | { type: "removeCorridor"; corridor: SerializedCorridor }
  | { type: "labelRoom"; roomId: string; oldLabel: string; newLabel: string }
  | {
      type: "moveRooms";
      roomIds: string[];
      deltaQ: number;
      deltaR: number;
      roomsBefore: SerializedRoom[]; // Rooms at original positions
      corridorsBefore: SerializedCorridor[];
      corridorsAfter: SerializedCorridor[];
    }
  | {
      type: "mergeRooms";
      removedRooms: SerializedRoom[];
      newRoom: SerializedRoom;
      removedCorridors: SerializedCorridor[];
      updatedCorridors: CorridorUpdate[];
    }
  | {
      type: "carveRoom";
      originalRoomId: string;
      originalHexes: string[];
      carvedRoom: SerializedRoom;
    }
  | {
      type: "bisectRoom";
      originalRoom: SerializedRoom;
      newRooms: [SerializedRoom, SerializedRoom];
      removedCorridors: SerializedCorridor[];
      addedCorridors: SerializedCorridor[];
    }
  | { type: "extendRoom"; roomId: string; addedHexes: string[] }
  | {
      type: "scaleRooms";
      roomChanges: Array<{
        roomId: string;
        addedHexes: string[];
        removedHexes: string[];
      }>;
      pushedRooms: Array<{
        roomId: string;
        deltaQ: number;
        deltaR: number;
        roomBefore: SerializedRoom;
      }>;
      corridorsBefore: SerializedCorridor[];
      corridorsAfter: SerializedCorridor[];
    }
  | { type: "deleteScribble"; rooms: SerializedRoom[]; corridors: SerializedCorridor[] }
  | { type: "initial" };

/**
 * A node in the undo tree representing a single operation.
 */
export interface UndoNode {
  id: string;
  parentId: string | null;
  childIds: string[];
  timestamp: number;
  operationType: OperationType;
  operationLabel: string;
  delta: OperationDelta;
  activeChildIndex: number;
}

/**
 * The complete undo tree structure.
 */
export interface UndoTree {
  nodes: Map<string, UndoNode>;
  rootId: string;
  currentId: string;
}

/**
 * Generate a unique ID for tree nodes.
 */
function generateNodeId(): string {
  return crypto.randomUUID();
}

/**
 * Create an empty undo tree with an initial node.
 */
export function createUndoTree(): UndoTree {
  const rootId = generateNodeId();
  const rootNode: UndoNode = {
    id: rootId,
    parentId: null,
    childIds: [],
    timestamp: Date.now(),
    operationType: "initial",
    operationLabel: "Initial state",
    delta: { type: "initial" },
    activeChildIndex: 0,
  };

  const nodes = new Map<string, UndoNode>();
  nodes.set(rootId, rootNode);

  return {
    nodes,
    rootId,
    currentId: rootId,
  };
}

/**
 * Add a new node as a child of the current node.
 * Returns a new tree (immutable update).
 */
export function addNode(
  tree: UndoTree,
  operationType: OperationType,
  operationLabel: string,
  delta: OperationDelta
): UndoTree {
  const newNodeId = generateNodeId();
  const currentNode = tree.nodes.get(tree.currentId);

  if (!currentNode) {
    throw new Error("Current node not found in tree");
  }

  // Create new node
  const newNode: UndoNode = {
    id: newNodeId,
    parentId: tree.currentId,
    childIds: [],
    timestamp: Date.now(),
    operationType,
    operationLabel,
    delta,
    activeChildIndex: 0,
  };

  // Clone nodes map
  const newNodes = new Map(tree.nodes);

  // Update parent to include new child
  const updatedParent: UndoNode = {
    ...currentNode,
    childIds: [...currentNode.childIds, newNodeId],
    activeChildIndex: currentNode.childIds.length, // Point to new child
  };

  newNodes.set(tree.currentId, updatedParent);
  newNodes.set(newNodeId, newNode);

  return {
    nodes: newNodes,
    rootId: tree.rootId,
    currentId: newNodeId,
  };
}

/**
 * Move to parent (undo).
 * Returns new tree with currentId pointing to parent.
 */
export function undo(tree: UndoTree): UndoTree {
  const currentNode = tree.nodes.get(tree.currentId);

  if (!currentNode || !currentNode.parentId) {
    return tree; // At root, can't undo
  }

  return {
    ...tree,
    currentId: currentNode.parentId,
  };
}

/**
 * Move to active child (redo).
 * Returns new tree with currentId pointing to active child.
 */
export function redo(tree: UndoTree): UndoTree {
  const currentNode = tree.nodes.get(tree.currentId);

  if (!currentNode || currentNode.childIds.length === 0) {
    return tree; // No children, can't redo
  }

  const activeChildId = currentNode.childIds[currentNode.activeChildIndex];

  return {
    ...tree,
    currentId: activeChildId,
  };
}

/**
 * Navigate to a specific node by ID.
 * Updates activeChildIndex along the path for future redo operations.
 */
export function navigateToNode(tree: UndoTree, targetId: string): UndoTree {
  if (!tree.nodes.has(targetId)) {
    return tree; // Target not found
  }

  if (tree.currentId === targetId) {
    return tree; // Already there
  }

  // Find paths from root to current and target
  const currentPath = getPathToRoot(tree, tree.currentId);
  const targetPath = getPathToRoot(tree, targetId);

  // Find common ancestor
  const currentSet = new Set(currentPath);
  let commonAncestor: string | null = null;

  for (const nodeId of targetPath) {
    if (currentSet.has(nodeId)) {
      commonAncestor = nodeId;
      break;
    }
  }

  if (!commonAncestor) {
    throw new Error("No common ancestor found - tree is corrupted");
  }

  // Clone nodes map for updates
  const newNodes = new Map(tree.nodes);

  // Update activeChildIndex for nodes on path from common ancestor to target
  const pathFromAncestorToTarget = targetPath.slice(
    0,
    targetPath.indexOf(commonAncestor)
  );

  for (let i = pathFromAncestorToTarget.length - 1; i >= 0; i--) {
    const nodeId = pathFromAncestorToTarget[i];
    const parentId = i < pathFromAncestorToTarget.length - 1
      ? pathFromAncestorToTarget[i + 1]
      : commonAncestor;

    const parentNode = newNodes.get(parentId);
    if (parentNode) {
      const childIndex = parentNode.childIds.indexOf(nodeId);
      if (childIndex !== -1 && childIndex !== parentNode.activeChildIndex) {
        newNodes.set(parentId, {
          ...parentNode,
          activeChildIndex: childIndex,
        });
      }
    }
  }

  return {
    nodes: newNodes,
    rootId: tree.rootId,
    currentId: targetId,
  };
}

/**
 * Get path from a node to the root (node first, root last).
 */
export function getPathToRoot(tree: UndoTree, nodeId: string): string[] {
  const path: string[] = [];
  let current = nodeId;

  while (current) {
    path.push(current);
    const node = tree.nodes.get(current);
    if (!node || !node.parentId) break;
    current = node.parentId;
  }

  return path;
}

/**
 * Get path from root to a node (root first, node last).
 */
export function getPathFromRoot(tree: UndoTree, nodeId: string): string[] {
  return getPathToRoot(tree, nodeId).reverse();
}

/**
 * Check if undo is available.
 */
export function canUndo(tree: UndoTree): boolean {
  const node = tree.nodes.get(tree.currentId);
  return !!node?.parentId;
}

/**
 * Check if redo is available.
 */
export function canRedo(tree: UndoTree): boolean {
  const node = tree.nodes.get(tree.currentId);
  return !!node && node.childIds.length > 0;
}

/**
 * Get the current node.
 */
export function getCurrentNode(tree: UndoTree): UndoNode | undefined {
  return tree.nodes.get(tree.currentId);
}

/**
 * Prune the tree, making the current node the new root.
 * Discards all ancestor nodes (undo history).
 */
export function pruneToNode(tree: UndoTree, nodeId: string): UndoTree {
  const node = tree.nodes.get(nodeId);
  if (!node) return tree;

  // Collect all nodes reachable from the target node (it and its descendants)
  const newNodes = new Map<string, UndoNode>();

  function collectDescendants(id: string): void {
    const n = tree.nodes.get(id);
    if (!n) return;
    newNodes.set(id, n);
    for (const childId of n.childIds) {
      collectDescendants(childId);
    }
  }

  collectDescendants(nodeId);

  // Update the target node to be the new root (no parent)
  const newRoot: UndoNode = {
    ...node,
    parentId: null,
    operationType: "initial",
    operationLabel: "Initial state",
    delta: { type: "initial" },
  };
  newNodes.set(nodeId, newRoot);

  return {
    nodes: newNodes,
    rootId: nodeId,
    currentId: nodeId,
  };
}

/**
 * Get tree metrics for visualization.
 */
export function getTreeMetrics(tree: UndoTree): { maxDepth: number; nodeCount: number } {
  let maxDepth = 0;

  function traverse(nodeId: string, depth: number): void {
    maxDepth = Math.max(maxDepth, depth);
    const node = tree.nodes.get(nodeId);
    if (node) {
      for (const childId of node.childIds) {
        traverse(childId, depth + 1);
      }
    }
  }

  traverse(tree.rootId, 0);

  return {
    maxDepth,
    nodeCount: tree.nodes.size,
  };
}
